import { TestSearchResultFromAPI } from "../types";
import {
    FILE_VALIDATION_ENDPOINT,
    TEST_ENDPOINT,
    PRESIGNED_BUCKET_URL_ENDPOINT,
} from "./endpoints";
import { fetcher } from "./utils";

export const getTestsByQuery = async ({
    query,
}: {
    query: string;
}): Promise<Array<TestSearchResultFromAPI> | null> => {
    try {
        const response = await fetcher(`${TEST_ENDPOINT}?query=${query}`);

        return response;
    } catch (error) {
        console.error("Get test error:", error);
        return null;
    }
};

export const getTestsInProgress =
    async (): Promise<Array<TestSearchResultFromAPI> | null> => {
        try {
            const response = await fetcher(`${TEST_ENDPOINT}?in-process=true`);

            return response;
        } catch (error) {
            console.error("Get test error:", error);
            return null;
        }
    };

export interface PresignedBucketResponse {
    bucket: string;
    key: string;
    presigned: {
        url: string;
        fields: Record<string, string>;
    };
    max_size_bytes: number;
    allowed_extensions: string[];
}

export const getPresignedBucketUrl = async ({
    filename,
    digest,
}: {
    filename: string;
    digest: string;
}): Promise<
    | { data: PresignedBucketResponse; error: null }
    | { data: null; error: string }
> => {
    try {
        const response = await fetcher(PRESIGNED_BUCKET_URL_ENDPOINT, {
            method: "POST",
            data: {
                filename,
                digest,
            },
        });

        return {
            data: response as PresignedBucketResponse,
            error: null,
        };
    } catch (error) {
        console.error("Get presigned bucket URL error:", error);
        return {
            data: null,
            error: "Failed to get presigned bucket URL",
        };
    }
};

export const uploadFileToS3 = async ({
    file,
    presignedData,
}: {
    file: File;
    presignedData: PresignedBucketResponse;
}): Promise<
    { success: true; error: null } | { success: false; error: string }
> => {
    try {
        const formData = new FormData();

        Object.entries(presignedData.presigned.fields).forEach(
            ([key, value]) => {
                formData.append(key, value);
            }
        );

        formData.append("file", file);

        const response = await fetch(presignedData.presigned.url, {
            method: "POST",
            body: formData,
        });

        if (response.ok) {
            return {
                success: true,
                error: null,
            };
        } else {
            return {
                success: false,
                error: `S3 upload failed with status: ${response.status}`,
            };
        }
    } catch (error) {
        console.error("S3 upload error:", error);
        return {
            success: false,
            error: "Failed to upload file to S3",
        };
    }
};

export interface ValidationResponse {
    message: string;
    code: "error" | "uploaded" | "clean" | "already_exists";
    results?: TestSearchResultFromAPI[];
}

export const validateUploadedFile = async ({
    digest,
}: {
    digest: string;
}): Promise<
    { data: ValidationResponse; error: null } | { data: null; error: string }
> => {
    try {
        const response = await fetcher(FILE_VALIDATION_ENDPOINT, {
            method: "POST",
            data: { digest },
        });

        return {
            data: response as ValidationResponse,
            error: null,
        };
    } catch (error) {
        console.error("File validation error:", error);
        return {
            data: null,
            error: "Failed to validate uploaded file",
        };
    }
};

const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

export const pollFileValidation = async ({
    digest,
}: {
    digest: string;
    maxAttempts?: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
}): Promise<
    { data: ValidationResponse; error: null } | { data: null; error: string }
> => {
    const DELAY_MS = 1000;
    const MAX_ATTEMPTS = 15;
    let attempt = 0;

    while (attempt < MAX_ATTEMPTS) {
        attempt++;

        const result = await validateUploadedFile({ digest });
        if (result.error || !result.data) {
            return result;
        }

        const response = result.data;

        if (response.code === "error") {
            return { data: null, error: response.message };
        }
        if (response.code === "clean" || response.code === "already_exists") {
            return { data: response, error: null };
        }
        if (response.code === "uploaded" && attempt >= MAX_ATTEMPTS) {
            return {
                data: null,
                error: response.message,
            };
        }

        if (response.code === "uploaded" && attempt < MAX_ATTEMPTS) {
            await sleep(DELAY_MS);
            continue;
        }

        return { data: null, error: "Unexpected validation response" };
    }
    return { data: null, error: "Max validation attempts reached" };
};

export const confirmFileUpload = async ({
    digest,
}: {
    digest: string;
}): Promise<
    { data: { test_id: number }; error: null } | { data: null; error: string }
> => {
    try {
        const response = await fetcher(TEST_ENDPOINT, {
            method: "POST",
            data: { digest },
        });

        return {
            data: response as { test_id: number },
            error: null,
        };
    } catch (error) {
        console.error("Confirm file upload error:", error);
        return {
            data: null,
            error: "Failed to confirm file upload",
        };
    }
};
